import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Zap, Crown, Package, ArrowRight, Check, PlugZap, RefreshCw, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { CREDIT_PACKS } from "@/lib/data/plan-data";
import { useCredits, creditPacks as legacyCreditPacks } from "@/contexts/CreditsContext";
import { useCreditPurchase } from "@/lib/hooks/use-credit-purchase";
import { useBilling } from "@/lib/hooks/use-billing";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { useUpdateAutoRechargeMutation } from "@/lib/graphql/types-and-hooks";
import { isLovablePreview } from "@/lib/utils/environment";
import { displayNumber } from "@/lib/i18n/display";

type Plan = "free" | "tall" | "grande";
type Mode = "plan" | "pack";

interface PlanOption {
  id: string;
  name: string;
  price: string; // "/user/mo"
  credits: number;
}

interface PackOption {
  id: string;
  credits: number;
  price: number;
  popular?: boolean;
}

const ALL_PLANS: PlanOption[] = [
  { id: "tall", name: "Tall", price: "$25/user/mo", credits: 1_000 },
  { id: "grande", name: "Grande", price: "$50/user/mo", credits: 2_000 },
];

/** Canonical credit packs — sourced from plan-data so the dialog stays in sync
 *  with the Plans page pop-up and the "What are credits used for?" FAQ. */
const PACKS: PackOption[] = CREDIT_PACKS.map(p => ({
  id: p.id,
  credits: p.credits,
  price: p.price,
  popular: "popular" in p ? p.popular : undefined,
}));

interface CreditLimitWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current plan. Derived from live billing (preview: Grande) when omitted. */
  plan?: Plan;
  creditsUsed?: number;
  /** Credits included per seat per month on the current plan. */
  creditsPerSeat?: number;
  /** Number of paid seats on the workspace. Workspace-wide credit pool is
   *  creditsPerSeat × seats. Derived from billing when omitted. */
  seats?: number;
  /** Context this dialog is shown in.
   *  - "credits" (default): low on credits — shows usage bar + both plan upgrade and credit pack options.
   *  - "integrations": locked feature — shows only the plan upgrade option (credit packs cannot unlock features). */
  reason?: "credits" | "integrations";
  /** Severity of the credit warning:
   *  - "out" (default): workspace is fully out of credits.
   *  - "low": workspace is at/below 15% remaining but not yet zero.
   *  Derived from the live balance when omitted. */
  severity?: "low" | "out";
  /** Credits an attempted action (or the current overage) requires. When set,
   *  the pack list is filtered to packs that cover the shortfall and the
   *  smallest covering pack is preselected. */
  creditsNeeded?: number;
  /** Non-dismissible hard gate: hide the close button + "Not now", and block
   *  Esc / overlay close. Used by the offline-overage gate (must buy before
   *  proceeding). */
  blocking?: boolean;
  /** Called after a successful credit-pack purchase (before the dialog closes). */
  onComplete?: () => void;
  /** Override DialogContent class — e.g. z-index escape over high-z popovers. */
  contentClassName?: string;
}

export default function CreditLimitWarningDialog({
  open,
  onOpenChange,
  plan: planProp,
  creditsUsed: creditsUsedProp,
  creditsPerSeat: creditsPerSeatProp,
  seats: seatsProp,
  reason = "credits",
  severity: severityProp,
  creditsNeeded,
  blocking,
  onComplete,
  contentClassName,
}: CreditLimitWarningDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const preview = isLovablePreview();
  const legacy = useCredits();
  const { billingInfo } = useBilling();
  const { balance: realBalance, purchaseCreditPack, purchasing } = useCreditPurchase();
  const workspaceId = useWorkspaceId();
  const [updateAutoRecharge] = useUpdateAutoRechargeMutation();

  // Derive billing context from live data (preview: legacy CreditsContext) when
  // the caller doesn't pass it explicitly, so any call site can open the dialog
  // with just `open`/`creditsNeeded`. Explicit props always win (the banner
  // feeds it real per-seat/usage numbers and its preview overrides).
  const balance = preview ? legacy.balance : realBalance;
  const planNameRaw = (billingInfo?.planName ?? (preview ? "Grande" : "Free")).toLowerCase();
  const plan: Plan =
    planProp ?? (planNameRaw === "grande" ? "grande" : planNameRaw === "tall" ? "tall" : "free");
  const seats = seatsProp ?? billingInfo?.seats ?? (preview ? 5 : 1);
  const subLimit = billingInfo?.subscriptionCreditsLimit ?? null;
  const creditsPerSeat =
    creditsPerSeatProp ?? (subLimit != null && seats > 0 ? Math.round(subLimit / seats) : undefined);
  const severity: "low" | "out" = severityProp ?? (balance <= 0 ? "out" : "low");

  const perSeat =
    creditsPerSeat ?? (plan === "grande" ? 2000 : plan === "tall" ? 1000 : 500);
  const totalCredits = perSeat * seats;
  const planLabel = plan === "grande" ? "Grande" : plan === "tall" ? "Tall" : "Free";
  const showUpgrade = plan === "free" || plan === "tall";
  const hidePack = reason === "integrations";
  const planOptions = showUpgrade
    ? ALL_PLANS.filter(p => (plan === "free" ? true : p.id !== "tall"))
    : [];

  // Shortfall = credits the action/overage needs beyond the current balance.
  // When positive, restrict the pack list to packs that fully cover it (matches
  // the offline-overage spec: must buy a pack that exceeds the overage).
  const shortfall = creditsNeeded ? Math.max(0, creditsNeeded - balance) : 0;
  const coveringPacks = shortfall > 0 ? PACKS.filter(p => p.credits >= shortfall) : PACKS;
  // Fall back to all packs if the shortfall exceeds the largest pack so the
  // select is never empty (those customers are contacted directly per spec).
  const availablePacks = coveringPacks.length > 0 ? coveringPacks : PACKS;

  const [mode, setMode] = useState<Mode>(showUpgrade || hidePack ? "plan" : "pack");
  const [selectedPlan, setSelectedPlan] = useState<string>(planOptions[0]?.id ?? "tall");
  const [selectedPack, setSelectedPack] = useState<string>(
    availablePacks.find(p => p.price === 50)?.id ?? availablePacks[0]?.id ?? "pack_2000",
  );
  const [autoRenew, setAutoRenew] = useState(false);

  // Low: ~85% consumed (matches the banner threshold). Out: fully consumed.
  const pct = severity === "low" ? 85 : 100;
  const usedCredits =
    creditsUsedProp ?? billingInfo?.totalCreditsUsed ?? Math.round((totalCredits * pct) / 100);

  const planChoice = planOptions.find(p => p.id === selectedPlan);
  // Keep the selection valid if the shortfall filtered out the current pick.
  const packChoice =
    availablePacks.find(p => p.id === selectedPack) ?? availablePacks[0];

  const ctaLabel =
    mode === "plan" && planChoice
      ? t("credits.upgradeToPlan", { plan: planChoice.name })
      : !hidePack && packChoice
      ? autoRenew
        ? t("credits.buyPackAutoRenew")
        : t("credits.buyPack")
      : t("credits.continueAction");

  const handleCta = async () => {
    // Plan upgrade → route to the Plans page (checkout / billing-portal flow).
    if (mode === "plan" || hidePack) {
      onOpenChange(false);
      navigate("/plans");
      return;
    }
    if (!packChoice) return;

    if (preview) {
      const legacyPack = legacyCreditPacks.find(p => p.credits === packChoice.credits);
      if (legacyPack) legacy.purchasePack(legacyPack);
      toast.success(t("credits.packPurchased", { credits: displayNumber(packChoice.credits) }), {
        description: t("credits.packPurchasedDescription"),
      });
      onComplete?.();
      onOpenChange(false);
      return;
    }

    try {
      const result = await purchaseCreditPack(packChoice.id);
      // No card on file → the hook redirects to Stripe Checkout (never resolves).
      if (!result.success) return;

      toast.success(t("credits.packPurchased", { credits: displayNumber(packChoice.credits) }), {
        description: t("credits.packPurchasedDescription"),
      });
      // Persist the auto-renew opt-in so OverageAutoReload can top up next time.
      if (autoRenew && workspaceId) {
        updateAutoRecharge({
          variables: { input: { workspaceId, enabled: true, packId: packChoice.id } },
        }).catch(() => {
          /* non-fatal — the purchase already succeeded */
        });
      }
      onComplete?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(t("credits.purchaseFailed"), {
        description:
          error instanceof Error ? error.message : t("credits.purchaseFailedDescription"),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn("max-w-md", contentClassName)}
        hideClose={blocking}
        onEscapeKeyDown={blocking ? (e) => e.preventDefault() : undefined}
        onInteractOutside={blocking ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            {reason === "integrations" ? (
              <div className="h-8 w-8 rounded-md bg-accent/15 flex items-center justify-center">
                <PlugZap className="h-4 w-4 text-accent" />
              </div>
            ) : (
              <div className="h-8 w-8 rounded-md bg-amber-500/15 flex items-center justify-center">
                <Zap className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </div>
            )}
            <div>
              <DialogTitle className="text-base">
                {reason === "integrations"
                  ? t("credits.integrationsTitle")
                  : severity === "low"
                  ? t("credits.lowDialogTitle")
                  : t("credits.outTitle")}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {reason === "integrations"
                  ? t("credits.integrationsDescription")
                  : severity === "low"
                  ? t("credits.usedOfTotalThisMonth", {
                      used: displayNumber(usedCredits),
                      total: displayNumber(totalCredits),
                    })
                  : t("credits.usedAllThisMonth", { total: displayNumber(totalCredits) })}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Usage bar (credits context only) */}
        {reason === "credits" && (
        <div className="rounded-md bg-muted/50 px-3 py-2">
          <div className="flex items-center justify-between text-[11px] mb-1.5">
            <span className="text-muted-foreground">
              {t("credits.usageBarSummary", {
                plan: planLabel,
                perSeat: displayNumber(perSeat),
                count: seats,
              })}
            </span>
            <span className="font-semibold">
              {displayNumber(usedCredits)} / {displayNumber(totalCredits)}
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className={cn("h-full", severity === "low" ? "bg-amber-500" : "bg-rose-500")}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
        )}

        {/* Choice */}
        <RadioGroup
          value={mode}
          onValueChange={v => setMode(v as Mode)}
          className="space-y-2"
        >
          {showUpgrade && (
            <ChoiceRow
              value="plan"
              active={mode === "plan"}
              icon={<Crown className="h-4 w-4 text-accent" />}
              title={t("credits.upgradePlanTitle")}
              subtitle={
                reason === "integrations"
                  ? t("credits.upgradeIntegrationsSubtitle")
                  : t("credits.upgradeSubtitle")
              }
            >
              <Select value={selectedPlan} onValueChange={setSelectedPlan} disabled={mode !== "plan"}>
                <SelectTrigger className="h-9 mt-2 text-xs">
                  <SelectValue>
                    {planChoice ? (
                      <>
                        <span className="font-medium">{planChoice.name}</span>
                        <span className="text-muted-foreground ml-1.5">
                          {t("credits.planOptionMeta", {
                            price: planChoice.price,
                            credits: displayNumber(planChoice.credits),
                          })}
                        </span>
                      </>
                    ) : null}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {planOptions.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="font-medium">{p.name}</span>
                      <span className="text-muted-foreground ml-1.5">
                        {t("credits.planOptionMeta", {
                          price: p.price,
                          credits: displayNumber(p.credits),
                        })}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </ChoiceRow>
          )}

          {!hidePack && (
          <ChoiceRow
            value="pack"
            active={mode === "pack"}
            icon={<Package className="h-4 w-4 text-accent" />}
            title={t("credits.packTitle")}
            subtitle={t("credits.packSubtitle")}
          >
            <Select value={selectedPack} onValueChange={setSelectedPack} disabled={mode !== "pack"}>
              <SelectTrigger className="h-9 mt-2 text-xs">
                <SelectValue>
                  {packChoice ? (
                    <>
                      <span className="font-medium">
                        {t("credits.creditsAmount", { credits: displayNumber(packChoice.credits) })}
                      </span>
                      <span className="text-muted-foreground ml-1.5">
                        {t("credits.packMeta", {
                          price: displayNumber(packChoice.price),
                          perCredit: (packChoice.price / packChoice.credits).toFixed(3),
                        })}
                      </span>
                      {packChoice.popular && (
                        <span className="ml-1.5 text-[9px] uppercase tracking-wide bg-accent/15 text-accent px-1 py-0.5 rounded">
                          {t("credits.popular")}
                        </span>
                      )}
                    </>
                  ) : null}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {availablePacks.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    <span className="font-medium">
                      {t("credits.creditsAmount", { credits: displayNumber(p.credits) })}
                    </span>
                    <span className="text-muted-foreground ml-1.5">
                      {t("credits.packMeta", {
                        price: displayNumber(p.price),
                        perCredit: (p.price / p.credits).toFixed(3),
                      })}
                    </span>
                    {p.popular && (
                      <span className="ml-1.5 text-[9px] uppercase tracking-wide bg-accent/15 text-accent px-1 py-0.5 rounded">
                        {t("credits.popular")}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Auto-renew toggle — buys the same pack again whenever the
             *  workspace next runs out of credits. */}
            <div
              className={cn(
                "mt-2 flex items-start gap-2 rounded-md border px-2.5 py-2 transition-colors",
                mode !== "pack"
                  ? "border-border/60 opacity-60"
                  : autoRenew
                  ? "border-accent/50 bg-accent/5"
                  : "border-border",
              )}
            >
              <RefreshCw className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <Label
                  htmlFor="credit-pack-auto-renew"
                  className="text-xs font-medium cursor-pointer"
                >
                  {t("credits.autoRenewLabel")}
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                  {packChoice
                    ? t("credits.autoRenewDescription", {
                        credits: displayNumber(packChoice.credits),
                        price: displayNumber(packChoice.price),
                      })
                    : t("credits.autoRenewDescriptionGeneric")}
                </p>
              </div>
              <Switch
                id="credit-pack-auto-renew"
                checked={autoRenew}
                onCheckedChange={setAutoRenew}
                disabled={mode !== "pack"}
              />
            </div>
          </ChoiceRow>
          )}
        </RadioGroup>

        <div className="flex items-center justify-end gap-2 pt-1">
          {!blocking && (
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              {t("credits.notNow")}
            </Button>
          )}
          <Button size="sm" className="gap-1.5" onClick={handleCta} disabled={purchasing}>
            {purchasing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <>
                {ctaLabel}
                <ArrowRight className="h-3.5 w-3.5" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChoiceRow({
  value,
  active,
  icon,
  title,
  subtitle,
  children,
}: {
  value: string;
  active: boolean;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <label
      htmlFor={`credit-mode-${value}`}
      className={cn(
        "block rounded-lg border px-3 py-3 cursor-pointer transition-colors",
        active
          ? "border-accent bg-accent/5"
          : "border-border hover:border-muted-foreground/30",
      )}
    >
      <div className="flex items-start gap-2.5">
        <RadioGroupItem id={`credit-mode-${value}`} value={value} className="mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            {icon}
            <span className="text-sm font-semibold">{title}</span>
            {active && <Check className="h-3.5 w-3.5 text-accent ml-auto" />}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
          {children}
        </div>
      </div>
    </label>
  );
}

/** Preview wrapper for the hidden UI menu. */
export function CreditLimitWarningDialogPreview() {
  const { t } = useTranslation();
  const [open, setOpen] = useState<{ plan: Plan; severity: "low" | "out" } | null>(null);
  const plans: Plan[] = ["free", "tall", "grande"];
  return (
    <>
      <div className="space-y-3">
        <div className="space-y-1.5">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t("credits.previewLowHeading")}
          </p>
          <div className="flex flex-wrap gap-2">
            {plans.map(p => (
              <Button
                key={`low-${p}`}
                variant="outline"
                size="sm"
                onClick={() => setOpen({ plan: p, severity: "low" })}
                className="gap-1.5 capitalize"
              >
                <Zap className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" /> {t("credits.previewPlanButton", { plan: p })}
              </Button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t("credits.previewOutHeading")}
          </p>
          <div className="flex flex-wrap gap-2">
            {plans.map(p => (
              <Button
                key={`out-${p}`}
                variant="outline"
                size="sm"
                onClick={() => setOpen({ plan: p, severity: "out" })}
                className="gap-1.5 capitalize"
              >
                <Zap className="h-3.5 w-3.5 text-destructive" /> {t("credits.previewPlanButton", { plan: p })}
              </Button>
            ))}
          </div>
        </div>
      </div>
      {open && (
        <CreditLimitWarningDialog
          open
          onOpenChange={o => !o && setOpen(null)}
          plan={open.plan}
          severity={open.severity}
          seats={3}
        />
      )}
    </>
  );
}
